import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const SalaryDetailModal = ({
  isOpen,
  onClose,
  record,
  getEmployeeName,
  getEmployeeCode,
  formatCurrency,
  formatMonth,
  getStatusBadge
}) => {
  if (!record) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="salary-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Salary Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Employee Info */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">{getEmployeeName(record.employee_id)}</h3>
                <p className="text-sm text-slate-500">{getEmployeeCode(record.employee_id)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Month</p>
                <p className="font-medium">{formatMonth(record.month)}</p>
              </div>
            </div>
          </div>

          {/* Earnings & Deductions Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Earnings */}
            <div>
              <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                Earnings
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Basic Pay</span>
                  <span className="font-medium">{formatCurrency(record.basic_pay || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">HRA</span>
                  <span className="font-medium">{formatCurrency(record.hra || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">DA</span>
                  <span className="font-medium">{formatCurrency(record.da || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Special Allowance</span>
                  <span className="font-medium">{formatCurrency(record.special_allowance || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Conveyance</span>
                  <span className="font-medium">{formatCurrency(record.conveyance || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Medical</span>
                  <span className="font-medium">{formatCurrency(record.medical_allowance || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Overtime</span>
                  <span className="font-medium">{formatCurrency(record.overtime || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Bonus</span>
                  <span className="font-medium">{formatCurrency(record.bonus || 0)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <span className="font-semibold text-slate-900">Gross Earnings</span>
                  <span className="font-semibold text-emerald-600">{formatCurrency(record.gross_earnings || record.gross_salary || 0)}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div>
              <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                Deductions
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">PF</span>
                  <span className="font-medium">{formatCurrency(record.pf_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">ESI</span>
                  <span className="font-medium">{formatCurrency(record.esi_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">TDS</span>
                  <span className="font-medium">{formatCurrency(record.tds_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Professional Tax</span>
                  <span className="font-medium">{formatCurrency(record.professional_tax || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Leave Deduction</span>
                  <span className="font-medium">{formatCurrency(record.leave_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Loan Deduction</span>
                  <span className="font-medium">{formatCurrency(record.loan_deduction || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Advance</span>
                  <span className="font-medium">{formatCurrency(record.advance_deduction || 0)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <span className="font-semibold text-slate-900">Total Deductions</span>
                  <span className="font-semibold text-red-600">{formatCurrency(record.total_deductions || 0)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Net Pay */}
          <div className="bg-blue-50 rounded-lg p-4 flex justify-between items-center">
            <span className="font-semibold text-lg text-slate-900">Net Payable</span>
            <span className="font-bold text-2xl text-blue-600">{formatCurrency(record.net_salary || record.net_pay || 0)}</span>
          </div>

          {/* Status & Remarks */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div>
              <p className="text-xs text-slate-500 mb-1">Status</p>
              <span className={getStatusBadge(record.status)}>
                {record.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            </div>
            {record.remarks && (
              <div className="text-right">
                <p className="text-xs text-slate-500 mb-1">Remarks</p>
                <p className="text-sm text-slate-600">{record.remarks}</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalaryDetailModal;
